import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from './firebase';

const normaliseText = (value) => String(value || '').trim();

const normaliseLineItem = (item) => ({
  assetId: item.assetId || '',
  title: normaliseText(item.title),
  imageUrl: normaliseText(item.imageUrl),
  currency: item.currency || 'ZAR',
  price: normaliseText(item.price),
  unitCost: normaliseText(item.unitCost),
  profit: normaliseText(item.profit),
  quantity: Number(item.quantity) || 1,
  lineTotal: Number(item.lineTotal) || 0,
  lineCost: Number(item.lineCost) || 0,
  lineProfit: Number(item.lineProfit) || 0,
  selectedSize: normaliseText(item.selectedSize),
  selectedColor: normaliseText(item.selectedColor),
});

const createOrderNumber = () => `ORD-${Date.now().toString(36).toUpperCase()}`;

const CONFIRMED_STATUS = 'Confirmed';
const FULFILLED_STATUS = 'Fulfilled';
const CANCELLED_STATUS = 'Cancelled';

const normaliseUpdateEntry = (update) => ({
  id: update.id || `update-${Date.now()}`,
  message: normaliseText(update.message),
  createdAt: update.createdAt || new Date().toISOString(),
  authorId: normaliseText(update.authorId),
  authorName: normaliseText(update.authorName),
  authorRole: normaliseText(update.authorRole),
  sentCount: Number(update.sentCount) || 0,
  lastSentAt: update.lastSentAt || '',
  lastSentChannel: normaliseText(update.lastSentChannel),
});

const shouldAffectStock = (order = {}, status = order.status) => (
  normaliseText(status) === CONFIRMED_STATUS ||
  (order.source === 'pos' && normaliseText(status) === FULFILLED_STATUS)
);

const applyStockAdjustment = async (transaction, order, stockDirection) => {
  if (!stockDirection || !order?.linkId) return;

  const itemRefs = (order.items || [])
    .filter((item) => item.assetId)
    .map((item) => ({
      item,
      ref: doc(db, 'sharabl-links', order.linkId, 'items', item.assetId),
    }));
  const itemSnapshots = await Promise.all(itemRefs.map(({ ref }) => transaction.get(ref)));

  itemRefs.forEach(({ item, ref }, index) => {
    const itemSnapshot = itemSnapshots[index];
    if (!itemSnapshot.exists()) return;

    const currentQuantity = Number(itemSnapshot.data().quantity) || 0;
    const orderQuantity = Math.max(Number(item.quantity) || 0, 0);
    const nextQuantity = currentQuantity + (stockDirection * orderQuantity);

    if (nextQuantity < 0) {
      throw new Error(`Not enough quantity available for ${item.title || 'this item'}.`);
    }

    transaction.update(ref, { quantity: nextQuantity });
  });
};

export const orderService = {
  async listOrders() {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to view orders.');
    }

    const snapshot = await getDocs(query(
      collection(db, 'sharabl-orders'),
      where('ownerId', '==', auth.currentUser.uid),
    ));

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate().toISOString() : data.submittedAt,
      };
    }).sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
  },

  async submitOrder(link, customer, items, options = {}) {
    if (!link?.id) {
      throw new Error('This order is missing a saved link reference.');
    }

    const normalisedItems = (items || []).map(normaliseLineItem);
    const subtotal = normalisedItems.reduce((sum, item) => sum + item.lineTotal, 0);
    const totalCost = normalisedItems.reduce((sum, item) => sum + item.lineCost, 0);
    const totalProfit = normalisedItems.reduce((sum, item) => sum + item.lineProfit, 0);
    const orderType = ['onsite', 'offsite'].includes(options.orderType) ? options.orderType : '';
    const deliveryFee = orderType === 'offsite' ? Math.max(Number(options.deliveryFee) || 0, 0) : 0;

    if (!normalisedItems.length) {
      throw new Error('Add at least one item before checking out.');
    }

    const payload = {
      orderNumber: createOrderNumber(),
      linkId: link.id,
      linkSlug: link.slug || '',
      linkTitle: link.title || '',
      ownerId: link.ownerId || '',
      customer: {
        name: normaliseText(customer?.name),
        email: normaliseText(customer?.email),
        phone: normaliseText(customer?.phone),
        notes: normaliseText(customer?.notes),
      },
      orderType,
      tableNumber: orderType === 'onsite' ? normaliseText(options.tableNumber) : '',
      deliveryAddress: orderType === 'offsite' ? normaliseText(options.deliveryAddress) : '',
      deliveryFee,
      source: options.source === 'pos' ? 'pos' : 'online',
      paymentMethod: normaliseText(options.paymentMethod),
      currency: normalisedItems[0]?.currency || link.currency || 'ZAR',
      items: normalisedItems,
      subtotal,
      totalCost,
      totalProfit,
      total: subtotal + deliveryFee,
      status: options.source === 'pos' ? 'Fulfilled' : 'Pending',
      paymentStatus: options.source === 'pos' ? 'Paid' : 'Unpaid',
      isPaid: options.source === 'pos',
      submittedAt: serverTimestamp(),
    };

    if (options.source === 'pos') {
      const createdRef = doc(collection(db, 'sharabl-orders'));
      await runTransaction(db, async (transaction) => {
        await applyStockAdjustment(transaction, { ...payload, id: createdRef.id }, -1);
        transaction.set(createdRef, payload);
      });
      return { ...payload, id: createdRef.id, submittedAt: new Date().toISOString() };
    }

    const created = await addDoc(collection(db, 'sharabl-orders'), payload);
    return { ...payload, id: created.id, submittedAt: new Date().toISOString() };
  },

  async updateOrder(orderOrId, changes = {}) {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to update orders.');
    }

    const id = typeof orderOrId === 'string' ? orderOrId : orderOrId?.id;

    if (!id) {
      throw new Error('This order is missing an ID.');
    }

    const allowedChanges = {};
    if (changes.status) allowedChanges.status = normaliseText(changes.status);
    if (changes.paymentStatus) allowedChanges.paymentStatus = normaliseText(changes.paymentStatus);
    if (typeof changes.isPaid === 'boolean') allowedChanges.isPaid = changes.isPaid;
    if (Array.isArray(changes.updates)) allowedChanges.updates = changes.updates.map(normaliseUpdateEntry);

    const nextStatus = allowedChanges.status;
    const hasStatusChange = Boolean(nextStatus);
    const orderRef = doc(db, 'sharabl-orders', id);

    if (hasStatusChange) {
      await runTransaction(db, async (transaction) => {
        const orderSnapshot = await transaction.get(orderRef);
        if (!orderSnapshot.exists()) {
          throw new Error('Order not found.');
        }

        const order = { id, ...orderSnapshot.data() };
        const previousStatus = normaliseText(order.status) || 'Pending';
        const stockDirection = !shouldAffectStock(order, previousStatus) && shouldAffectStock(order, nextStatus)
          ? -1
          : shouldAffectStock(order, previousStatus) && nextStatus === CANCELLED_STATUS
            ? 1
            : 0;

        await applyStockAdjustment(transaction, order, stockDirection);

        transaction.update(orderRef, allowedChanges);
      });
    } else {
      await updateDoc(orderRef, allowedChanges);
    }

    return { id, ...allowedChanges };
  },

  async removeOrder(orderOrId) {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to remove orders.');
    }

    const id = typeof orderOrId === 'string' ? orderOrId : orderOrId?.id;

    if (!id) {
      throw new Error('This order is missing an ID.');
    }

    const orderRef = doc(db, 'sharabl-orders', id);

    await runTransaction(db, async (transaction) => {
      const orderSnapshot = await transaction.get(orderRef);
      if (!orderSnapshot.exists()) return;

      const order = { id, ...orderSnapshot.data() };
      if (shouldAffectStock(order) && order.linkId) {
        await applyStockAdjustment(transaction, order, 1);
      }

      transaction.delete(orderRef);
    });

    return id;
  },
};
